import { FC } from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  Chip,
  // CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import axios from 'server';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';

interface ServiceProviderConfig {
  sp_name: string;
  protocol: string;
  entity_id: string;
  metadata_url: string;
  acs_url: string;
  acs_binding: string;
  login_url: string;
  name_id_format: string;
  authn_requests_signed: boolean;
  want_assertions_signed: boolean;
}

const cardStyle = {
  background: '#fff',
  border: '1px solid #e6eaf0',
  borderRadius: 12,
  boxShadow: '0 3px 12px rgba(35, 45, 65, 0.06)',
  padding: '24px',
};

const readonlyFieldStyle = {
  backgroundColor: '#f7f9fc',
  borderRadius: 6,
};

const IdentityServiceTable: FC = () => {
  const {
    notificationStore: { openNotification },
  } = useStore();

  // const fetchProvider = async (): Promise<ServiceProviderConfig> => {
  //   const response = await axios.get('saml/sp-details');
  //   const raw = response.data?.data ?? response.data;

  //   // Supports an object response or an array response.
  //   const provider = Array.isArray(raw) ? raw[0] : Array.isArray(raw?.items) ? raw.items[0] : raw;

  //   if (!provider || !provider.sp_name) {
  //     throw new Error('SAML Service Provider configuration not found.');
  //   }

  //   return provider as ServiceProviderConfig;
  // };

  const fetchProvider = async (): Promise<ServiceProviderConfig> => {
    try {
      const response = await axios.get('saml/sp-details');

      const raw = response.data?.data ?? response.data;

      const provider = Array.isArray(raw) ? raw[0] : Array.isArray(raw?.items) ? raw.items[0] : raw;

      if (!provider || !provider.sp_name) {
        throw new Error('SAML Service Provider configuration not found.');
      }

      return provider as ServiceProviderConfig;
    } catch (error: any) {
      const message = error?.response?.data?.message;
      throw new Error(message || error?.message || 'Something went wrong.');
    }
  };
  const {
    data: provider,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery<ServiceProviderConfig, Error>('saml/sp-details', fetchProvider, {
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  useAppPreloader(isLoading || isFetching);

  const copyToClipboard = async (value: string | null | undefined, label: string) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      openNotification('success', `${label} copied to clipboard.`);
    } catch {
      openNotification('error', `Unable to copy ${label.toLowerCase()}.`);
    }
  };

  if (isLoading) {
    return <></>;
  }

  if (isError) {
    return (
      <Typography color='error' style={{ padding: 20 }}>
        {error?.message || 'Failed to load SAML configuration.'}
      </Typography>
    );
  }

  if (!provider) {
    return (
      <Typography style={{ padding: 20, textAlign: 'center' }}>
        No SAML Service Provider configuration found.
      </Typography>
    );
  }

  const connectionFields = [
    { label: 'SP Name', value: provider.sp_name },
    { label: 'Protocol', value: provider.protocol },
    { label: 'Entity ID / Issuer', value: provider.entity_id },
    { label: 'Metadata URL', value: provider.metadata_url },
    { label: 'Assertion Consumer Service (ACS) URL', value: provider.acs_url },
    { label: 'ACS Binding', value: provider.acs_binding },
    { label: 'Login URL', value: provider.login_url },
    { label: 'NameID Format', value: provider.name_id_format },
  ];

  const securitySettings = [
    {
      label: 'Authn Requests Signed',
      value: provider.authn_requests_signed,
    },
    {
      label: 'Assertions Signed',
      value: provider.want_assertions_signed,
    },
  ];

  return (
    <>
      <Typography
        variant='h5'
        style={{
          color: '#202b3c',
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        SAML Service Provider
      </Typography>

      <Typography variant='body1' style={{ color: '#687891', marginBottom: 22 }}>
        EasyIdentity connection details and SAML security settings.
      </Typography>

      {/* Connection details */}
      <Paper elevation={0} style={{ ...cardStyle, marginBottom: 24 }}>
        <Grid container alignItems='center' justify='space-between' spacing={2} style={{ marginBottom: 18 }}>
          <Grid item xs>
            <Typography variant='h6' style={{ color: '#202b3c', fontWeight: 700 }}>
              Connection details
            </Typography>

            <Typography variant='body2' style={{ color: '#687891', marginTop: 4 }}>
              Copy these values to configure your identity provider.
            </Typography>
          </Grid>

          <Grid item>
            <Button
              variant='contained'
              color='secondary'
              onClick={() => provider.metadata_url && window.open(provider.metadata_url, '_blank')}
              disabled={!provider.metadata_url}
              style={{
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
              }}
            >
              View Metadata
            </Button>
          </Grid>
        </Grid>

        <div
          style={{
            borderTop: '1px solid #edf0f4',
            marginBottom: 20,
          }}
        />

        <Grid container spacing={3}>
          {connectionFields.map((field) => (
            <Grid item xs={12} sm={6} key={field.label}>
              <Typography
                variant='body2'
                style={{
                  fontWeight: 600,
                  color: '#202b3c',
                  marginBottom: 8,
                }}
              >
                {field.label}
              </Typography>

              <Grid container spacing={1} alignItems='stretch'>
                <Grid item xs>
                  <TextField
                    fullWidth
                    variant='outlined'
                    size='small'
                    value={field.value ?? ''}
                    placeholder='-'
                    InputProps={{
                      readOnly: true,
                      style: {
                        ...readonlyFieldStyle,
                        fontSize: 13,
                      },
                    }}
                  />
                </Grid>

                <Grid item>
                  <Button
                    variant='outlined'
                    color='secondary'
                    disabled={!field.value}
                    onClick={() => copyToClipboard(field.value, field.label)}
                    style={{
                      height: 40,
                      minWidth: 72,
                      textTransform: 'none',
                    }}
                  >
                    Copy
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Security settings */}
      <Paper elevation={0} style={cardStyle}>
        <Typography
          variant='h6'
          style={{
            color: '#202b3c',
            fontWeight: 700,
            marginBottom: 6,
          }}
        >
          Security settings
        </Typography>

        <Typography variant='body2' style={{ color: '#687891', marginBottom: 20 }}>
          SAML request signing and assertion validation requirements.
        </Typography>

        <div style={{ borderTop: '1px solid #edf0f4' }}>
          {securitySettings.map((setting, index) => (
            <Grid
              container
              alignItems='center'
              justify='space-between'
              spacing={2}
              key={setting.label}
              style={{
                padding: '16px 0',
                borderBottom: index < securitySettings.length - 1 ? '1px solid #edf0f4' : 'none',
              }}
            >
              <Grid item xs>
                <Typography
                  variant='body2'
                  style={{
                    color: '#202b3c',
                    fontWeight: 600,
                  }}
                >
                  {setting.label}
                </Typography>
              </Grid>

              <Grid item>
                <Chip
                  label={setting.value ? 'Enabled' : 'Disabled'}
                  size='small'
                  style={{
                    fontWeight: 600,
                    backgroundColor: setting.value ? '#e5f7ed' : '#f1f3f6',
                    color: setting.value ? '#16865e' : '#687891',
                  }}
                />
              </Grid>
            </Grid>
          ))}
        </div>
      </Paper>
    </>
  );
};

export default IdentityServiceTable;
